import { Platform, StyleSheet, type StyleProp, type ViewStyle } from "@-/com.npmjs.react-native";
import { useHasPrimaryControlsStripOpaqueChrome } from "@-/dev.improve.haniverse.contexts.primary-controls-strip.opaque-chrome";
import { useResolveClassNames } from "@-/com.npmjs.uniwind";

/** The full fade below the pills, including the former 4-point opaque toolbar clearance. */
export const HEADER_SCROLL_CONTENT_GAP = Platform.OS === "web" ? 8 : 12;

/** Shared by plain scroll views, virtualized lists, and specialized scroll containers. */
export function useHeaderScrollContentStyle({
  contentContainerClassName = "",
  contentContainerStyle,
  headerOverlap = 0,
  horizontal = false,
  suppressContentGap = false,
}: {
  contentContainerClassName?: string;
  contentContainerStyle?: StyleProp<ViewStyle>;
  /** Height of a header floating inside this viewport; exclude chrome outside the viewport. */
  headerOverlap?: number;
  horizontal?: boolean | null;
  /**
   * Drop the gap's floor for containers that stand the header fade down, where the reserved
   * points clear nothing and instead read as a band above pinned content. Opaque accordion /
   * material-tab chrome also suppresses automatically via {@link useHasPrimaryControlsStripOpaqueChrome}.
   */
  suppressContentGap?: boolean;
}): StyleProp<ViewStyle> {
  const hasOpaqueChrome = useHasPrimaryControlsStripOpaqueChrome();
  const classStyle = useResolveClassNames(contentContainerClassName ?? "");
  if (horizontal) {
    return contentContainerStyle;
  }
  const resolved = StyleSheet.flatten([classStyle, contentContainerStyle]);
  const existingTop = resolved.paddingTop ?? resolved.paddingVertical ?? resolved.padding ?? 0;
  const requestedTop = typeof existingTop === "number" ? existingTop : 0;
  const dropGap = suppressContentGap || hasOpaqueChrome;
  return [
    contentContainerStyle,
    {
      paddingTop:
        headerOverlap +
        (dropGap ? requestedTop : Math.max(HEADER_SCROLL_CONTENT_GAP, requestedTop)),
    },
  ];
}
